import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { ShoppingCart, Music, QrCode, X } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { usePageEditMode } from '@/context/PageEditModeContext';
import { adminFetch } from '@/utils/adminAuth';

const API = process.env.REACT_APP_BACKEND_URL;

/** Small helper: contenteditable span that PUTs the album back to the backend on blur. */
const DbEditable = ({ enabled, album, field, parse = (v) => v, format = (v) => String(v ?? ''), className, testid }) => {
  if (!enabled) return <span className={className} data-testid={testid}>{format(album[field])}</span>;
  const save = async (e) => {
    const raw = e.currentTarget.textContent.trim();
    let value;
    try { value = parse(raw); } catch { toast.error('Invalid value'); return; }
    if (value === album[field]) return;
    try {
      const payload = { ...album, [field]: value };
      delete payload._id; delete payload.track_ids; delete payload.track_count;
      const r = await adminFetch(`/admin/albums/${album.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      album[field] = value; // mutate so re-render on parent refresh is seamless
      toast.success(`Updated ${field}`);
    } catch (err) {
      toast.error(err.message || 'Save failed');
    }
  };
  return (
    <span
      contentEditable
      suppressContentEditableWarning
      onBlur={save}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      className={`${className || ''} outline-none ring-1 ring-primary/40 focus:ring-primary rounded px-1 cursor-text`}
      data-testid={testid}
    >{format(album[field])}</span>
  );
};

export const AlbumCard = ({ album }) => {
  const { addToCart } = useCart();
  const [showQR, setShowQR] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const { enabled: editEnabled } = usePageEditMode();

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await addToCart('album', album.id);
      toast.success('Album added to cart!');
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Already in cart');
      } else {
        toast.error('Failed to add to cart');
      }
    }
  };

  const handleShowQR = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!qrCode) {
      try {
        const res = await fetch(`${API}/api/quick-buy/qr/album/${album.id}/base64`);
        const data = await res.json();
        setQrCode(data.qr_code);
      } catch (err) {
        console.error('Failed to load QR code:', err);
      }
    }
    setShowQR(true);
  };

  return (
    <Link to={`/quick-buy/album/${album.id}`} onClick={(e) => { if (editEnabled) e.preventDefault(); }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        whileHover={{ y: -4 }}
        className="group relative bg-card border border-border/50 rounded-2xl overflow-hidden hover:border-primary/50 transition-all duration-300 hover:shadow-2xl"
        data-testid={`album-card-${album.id}`}
      >
        {/* Album Art */}
        <div className="relative aspect-square overflow-hidden">
          <img loading="lazy" decoding="async"
            src={album.cover_url}
            alt={album.title}
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          
          {/* Bestseller Badge */}
          {album.bestseller && (
            <div className="absolute top-3 right-3 px-3 py-1.5 bg-gradient-to-r from-orange-500 to-red-500 text-white text-xs font-bold rounded-full shadow-lg flex items-center gap-1 animate-pulse" data-testid={`album-bestseller-badge-${album.id}`}>
              <span>⭐</span>
              BESTSELLER
            </div>
          )}
          
          {/* Album Badge */}
          <div className="absolute top-3 left-3 px-3 py-1 bg-secondary/90 backdrop-blur-sm text-secondary-foreground text-xs font-bold rounded-full flex items-center gap-2" data-testid={`album-badge-${album.id}`}>
            <Music className="w-3 h-3" />
            ALBUM
          </div>
        </div>

        {/* Album Info */}
        <div className="p-5">
          <h3 className="text-lg font-bold text-foreground mb-1 truncate">
            <DbEditable
              enabled={editEnabled}
              album={album}
              field="title"
              testid={`album-title-${album.id}`}
            />
          </h3>
          <p className="text-sm text-muted-foreground mb-3 truncate">
            <DbEditable
              enabled={editEnabled}
              album={album}
              field="artist"
              testid={`album-artist-${album.id}`}
            />
          </p>

          {/* Track Count & Price */}
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs text-muted-foreground" data-testid={`album-track-count-${album.id}`}>
              {album.track_ids?.length || 0} tracks
            </span>
            <span className="text-xl font-bold text-primary font-display">
              $<DbEditable
                enabled={editEnabled}
                album={album}
                field="price"
                parse={(v) => parseFloat(v.replace(/[^\d.]/g, ''))}
                format={(v) => (typeof v === 'number' ? v.toFixed(2) : '0.00')}
                testid={`album-price-${album.id}`}
              />
            </span>
          </div>

          {/* Add to Cart Button */}
          <div className="space-y-3">
            <button
              onClick={handleAddToCart}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground rounded-full font-bold transition-all hover:scale-105 shadow-glow"
              data-testid={`album-add-to-cart-btn-${album.id}`}
            >
              <ShoppingCart className="w-4 h-4" />
              Add to Cart
            </button>
            <button
              onClick={handleShowQR}
              className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-500 hover:to-purple-500 text-white rounded-full font-bold text-lg transition-all hover:scale-105 shadow-lg"
              data-testid={`album-qr-btn-${album.id}`}
            >
              <QrCode className="w-6 h-6" />
              SCAN TO BUY INSTANTLY
            </button>
          </div>
        </div>
      </motion.div>

      {/* QR Code Lightbox — rendered in a portal so parent transforms don't distort it */}
      {showQR && createPortal(
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center"
          onClick={() => setShowQR(false)}
        >
          {/* Dark overlay */}
          <div className="absolute inset-0 bg-black/85" />
          
          {/* Lightbox content */}
          <div 
            className="relative bg-white rounded-2xl p-8 max-w-xs w-full mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setShowQR(false)}
              className="absolute -top-3 -right-3 w-10 h-10 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Album info */}
            <div className="flex items-center gap-2 justify-center mb-1">
              <Music className="w-4 h-4 text-purple-600" />
              <span className="text-xs font-bold text-purple-600 uppercase">Album</span>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-1 text-center">{album.title}</h3>
            <p className="text-gray-500 mb-4 text-center text-sm">{album.artist}</p>
            
            {/* QR Code */}
            <div className="bg-white p-2 rounded-xl border-4 border-gray-200 mb-4">
              {qrCode ? (
                <img loading="lazy" decoding="async" src={qrCode} alt="QR Code" className="w-full aspect-square" />
              ) : (
                <div className="w-full aspect-square bg-gray-100 rounded-lg flex items-center justify-center">
                  <div className="w-10 h-10 border-3 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
            
            {/* Price */}
            <p className="text-3xl font-bold text-primary mb-2 text-center">${album.price.toFixed(2)}</p>
            <p className="text-sm text-gray-500 text-center">Scan with your phone to buy & download all tracks</p>
            
            {/* Tap to close hint */}
            <p className="text-xs text-gray-400 text-center mt-4">Tap outside or X to close</p>
          </div>
        </div>,
        document.body
      )}
    </Link>
  );
};
